import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PhoneIncoming } from 'lucide-react'
import { useEffect } from 'react'
import { useNavigate } from 'react-router'

import { declineTransfer, fetchRingingTransfers } from '@/api/transfers'
import type { MyOrg } from '@/api/types'
import { Button } from '@/components/ui/button'

/** Without the push stream, rings are checked this often (a ring lasts 30 seconds). */
const FALLBACK_REFRESH_MS = 5000

/**
 * A call being handed to this person rings here, on any page: accept opens the call and takes it over from this
 * browser; decline lets the agent carry on. Pushed by the calls stream as soon as the transfer is made.
 */
export const IncomingTransfers = ({ org, userId }: { org: MyOrg; userId: string }) => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const canTakeCalls = org.permissions.includes('calls.transfer')
  const ringing = useQuery({
    queryKey: ['transfers-ringing', org.id],
    queryFn: () => fetchRingingTransfers(org.id),
    refetchInterval: FALLBACK_REFRESH_MS,
    enabled: canTakeCalls,
  })
  const decline = useMutation({
    mutationFn: (transferId: string) => declineTransfer(org.id, transferId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['transfers-ringing', org.id] }),
  })

  // A transfer for this person refreshes the ring at once.
  useEffect(() => {
    if (!canTakeCalls) return
    const source = new EventSource(`/api/orgs/${org.id}/calls/stream`)
    const onCall = (event: MessageEvent<string>) => {
      const change = JSON.parse(event.data) as { transfer?: { toUserId: string | null } }
      if (change.transfer?.toUserId === userId) {
        void queryClient.invalidateQueries({ queryKey: ['transfers-ringing', org.id] })
      }
    }
    source.addEventListener('call', onCall)
    return () => source.close()
  }, [org.id, userId, canTakeCalls, queryClient])

  const [transfer] = ringing.data?.transfers ?? []
  if (!transfer) return null
  return (
    <div
      role="alertdialog"
      aria-label="Incoming transfer"
      className="fixed right-4 bottom-4 z-50 flex w-80 flex-col gap-4 rounded-3xl bg-card p-5 shadow-lg motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2"
    >
      <div className="flex items-center gap-2 font-medium">
        <PhoneIncoming className="size-4 animate-pulse text-primary motion-reduce:animate-none" />
        {transfer.reason === 'asked_for_person' ? 'A shopper asked for a person' : 'A call for you'}
      </div>
      <p className="text-sm text-muted-foreground">Accept to take it over from this browser.</p>
      <div className="flex gap-2">
        <Button
          className="flex-1"
          onClick={() => void navigate(`/o/${org.slug}/calls/${transfer.callId}?accept=${transfer.id}`)}
        >
          Accept
        </Button>
        <Button
          variant="outline"
          className="flex-1"
          disabled={decline.isPending}
          onClick={() => decline.mutate(transfer.id)}
        >
          Decline
        </Button>
      </div>
    </div>
  )
}
